import type { CellType, CellValue, TableColumn, TableFilter, TableRow, TableView } from "./types";

const isEmpty = (value: CellValue): boolean =>
  value === null || value === undefined || value === "";

export function compareCells(a: CellValue, b: CellValue, type: CellType): number {
  if (isEmpty(a) && isEmpty(b)) return 0;
  if (isEmpty(a)) return 1;
  if (isEmpty(b)) return -1;

  if (type === "number") return Number(a) - Number(b);
  if (type === "checkbox") return Number(Boolean(b)) - Number(Boolean(a));
  if (type === "date") return new Date(String(a)).getTime() - new Date(String(b)).getTime();
  return String(a).localeCompare(String(b), "de", { sensitivity: "base", numeric: true });
}

function matchesFilter(row: TableRow, filter: TableFilter, type: CellType): boolean {
  const cell = row.cells[filter.columnId] ?? null;
  const text = String(cell ?? "").toLowerCase();
  const needle = String(filter.value ?? "").toLowerCase();

  switch (filter.operator) {
    case "contains":
      return text.includes(needle);
    case "notContains":
      return !text.includes(needle);
    case "eq":
      return type === "number" ? Number(cell) === Number(filter.value) : text === needle;
    case "neq":
      return type === "number" ? Number(cell) !== Number(filter.value) : text !== needle;
    case "gt":
      return Number(cell) > Number(filter.value);
    case "gte":
      return Number(cell) >= Number(filter.value);
    case "lt":
      return Number(cell) < Number(filter.value);
    case "lte":
      return Number(cell) <= Number(filter.value);
    case "isEmpty":
      return isEmpty(cell);
    case "isNotEmpty":
      return !isEmpty(cell);
    case "isTrue":
      return Boolean(cell);
    case "isFalse":
      return !cell;
    default:
      return true;
  }
}

export function applyTableView(
  rows: TableRow[],
  columns: TableColumn[],
  view: TableView,
): TableRow[] {
  const typeOf = (columnId: string): CellType =>
    columns.find((column) => column.id === columnId)?.type ?? "text";

  let result = rows;

  for (const filter of view.filters ?? []) {
    result = result.filter((row) => matchesFilter(row, filter, typeOf(filter.columnId)));
  }

  const search = view.search?.trim().toLowerCase();
  if (search) {
    result = result.filter((row) =>
      columns.some((column) => String(row.cells[column.id] ?? "").toLowerCase().includes(search)),
    );
  }

  const sort = view.sort;
  if (sort) {
    const type = typeOf(sort.columnId);
    const factor = sort.direction === "desc" ? -1 : 1;
    result = [...result].sort(
      (a, b) => compareCells(a.cells[sort.columnId] ?? null, b.cells[sort.columnId] ?? null, type) * factor,
    );
  }

  return result;
}
